/**
 * Per-(serviceKey × city) unique content generators.
 *
 * Purpose: defeat the duplicate-content trap on the 185 ville × service pages
 * by injecting truly localized, service-aware paragraphs, bullet tips,
 * testimonials and an extra FAQ question — all deterministic (SSR/SSG safe).
 *
 * Every helper takes (serviceKey, city) and returns content derived from
 *   - city geographic / habitat data (cities.js)
 *   - dept-level water hardness (regulated by ARS / agences de l'eau)
 *   - service-specific French repair vocabulary
 *
 * Deterministic = same input → same output (no Math.random) so the
 * pre-rendered HTML produced by react-snap matches what hydrates client-side.
 */

import { CITIES } from "../data/cities";

/* ─────────── 1. WATER HARDNESS PER DEPT ─────────── */
/**
 * Dureté de l'eau (°f, degrés français) par département IDF.
 * Source: ARS Île-de-France & Eau de Paris.
 * Pertinent pour lave-linge, lave-vaisselle, frigo (distributeur d'eau).
 */
const WATER_HARDNESS_BY_DEPT = {
    "Paris (75)": { th: 27, label: "très dure", note: "calcaire élevé typique du réseau parisien (≈ 27 °f)" },
    "Hauts-de-Seine (92)": { th: 25, label: "dure à très dure", note: "eau dure du réseau Sedif / Eau de Paris (22 à 30 °f selon secteur)" },
    "Yvelines (78)": { th: 22, label: "dure", note: "eau dure typique de l'ouest francilien (≈ 20 à 25 °f)" },
    "Val-de-Marne (94)": { th: 26, label: "très dure", note: "eau très calcaire en bord de Marne (≈ 25 à 30 °f)" },
};

export function getWaterHardness(city) {
    return WATER_HARDNESS_BY_DEPT[city.dept] || { th: 25, label: "dure", note: "eau calcaire typique de l'Île-de-France" };
}

/* ─────────── 2. NEAREST CITIES (haversine) ─────────── */
function haversineKm(a, b) {
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}

export function getNearestCities(citySlug, n = 8) {
    const me = CITIES.find((c) => c.slug === citySlug);
    if (!me) return [];
    return CITIES
        .filter((c) => c.slug !== citySlug)
        .map((c) => ({ ...c, _distKm: haversineKm(me.coords, c.coords) }))
        .sort((a, b) => a._distKm - b._distKm)
        .slice(0, n);
}

/* ─────────── 3. SERVICE-SPECIFIC LOCAL PARAGRAPHS ─────────── */
/**
 * Returns 2 paragraphs unique to the (serviceKey, city) combination.
 * The first paragraph mixes city habitat + service usage context.
 * The second paragraph addresses a service-specific local problem
 * (calcaire, condensation, encastrement…).
 */
export function getLocalServiceParagraphs(serviceKey, city) {
    const water = getWaterHardness(city);
    const firstLandmark = city.landmarks[0] || city.name;
    const mainNeighborhood = city.neighborhoods[0] || "centre-ville";

    const map = {
        "lave-linge": [
            `À ${city.name}, le parc immobilier (${city.habitat}) implique souvent des lave-linge installés dans des salles de bains compactes, des cuisines fermées ou des buanderies de sous-sol. Nos techniciens connaissent les contraintes d'accès locales (${city.access}) et arrivent équipés pour intervenir même dans les espaces réduits ou les étages élevés sans ascenseur de service.`,
            `Le réseau d'eau de ${city.dept} est ${water.label} (${water.note}), ce qui entartre la résistance, la pompe de vidange et le tambour de votre lave-linge plus vite qu'ailleurs. C'est la première cause d'arrêt prématuré à ${city.name} : nous vérifions systématiquement l'état de la résistance et de la pompe, et conseillons l'usage régulier d'un anti-calcaire adapté.`,
        ],
        "lave-vaisselle": [
            `À ${city.name}, beaucoup de cuisines (${city.habitat}) intègrent un lave-vaisselle encastré sous plan de travail, souvent avec habillage bois sur mesure. Nos techniciens démontent les façades sans rayure et remontent au mm près. Nous couvrons l'ensemble des quartiers — ${city.neighborhoods.slice(0, 4).join(", ")} — par ${city.access}.`,
            `L'eau de ${city.dept} étant ${water.label} (${water.note}), les bras de lavage, le filtre, la cuve et l'adoucisseur intégré s'entartrent rapidement. C'est la cause n°1 des "vaisselles mal lavées" à ${city.name}. Nous diagnostiquons sur place l'état de l'adoucisseur, de la résistance et de la pompe de cyclage avant tout devis.`,
        ],
        "frigo": [
            `Les logements de ${city.name} (${city.habitat}) abritent une grande variété de réfrigérateurs : combinés intégrés, frigos américains side-by-side (résidences récentes près de ${firstLandmark}), congélateurs armoire (caves et buanderies). Nos techniciens couvrent toutes ces configurations, y compris le froid ventilé, le no-frost et les distributeurs d'eau/glaçons.`,
            `Sur les frigos avec distributeur d'eau et glaçons (très répandus à ${city.name} dans les cuisines équipées), l'eau ${water.label} de ${city.dept} bouche le filtre et la conduite plus vite que la moyenne nationale. Nous remplaçons le filtre, détartrons l'évaporateur et vérifions le circuit de dégivrage en une seule intervention à domicile.`,
        ],
        "four": [
            `À ${city.name}, les fours (${city.habitat}) sont majoritairement encastrés en colonne ou sous plan, parfois associés à un micro-ondes combiné de la même marque. Nos techniciens interviennent sur four pyrolyse, catalyse et vapeur — toutes marques — directement à votre domicile dans le secteur ${mainNeighborhood} et alentours.`,
            `Spécificité locale : à ${city.name}, beaucoup de fours intégrés sont raccordés à des hottes filtrantes ou aspirantes anciennes (immeubles haussmanniens) qui surchauffent la zone et accélèrent l'usure du ventilateur tangentiel et de la carte électronique. Nous vérifions systématiquement la ventilation du caisson lors du diagnostic.`,
        ],
        "seche-linge": [
            `À ${city.name}, le sèche-linge est souvent installé en colonne avec le lave-linge (gain de place dans les logements ${city.habitat}). Nos techniciens interviennent sur les trois technologies — condensation, évacuation et pompe à chaleur — et démontent en toute sécurité les colonnes superposées sans abîmer vos appareils ni votre installation.`,
            `À ${city.name}, l'humidité urbaine et la densité d'habitat favorisent l'encrassement du condenseur et du filtre à peluches du sèche-linge à pompe à chaleur. C'est la cause n°1 des "cycles qui n'en finissent pas" et des codes erreur de chauffe. Nous nettoyons en profondeur l'échangeur thermique et le bac à condensats lors de l'intervention.`,
        ],
    };

    return map[serviceKey] || [city.intro];
}

/* ─────────── 4. SERVICE-LOCAL TIPS ─────────── */
export function getLocalServiceTips(serviceKey, city) {
    const water = getWaterHardness(city);
    const map = {
        "lave-linge": [
            `Vérifier le filtre de la pompe de vidange tous les 2 mois (eau ${water.label} à ${city.name})`,
            `Lancer un cycle 90 °C à vide avec vinaigre blanc une fois par trimestre pour limiter le calcaire`,
            `Ne pas surcharger : à ${city.name}, beaucoup d'appartements ont un sol non parfaitement plan, ce qui amplifie les vibrations`,
        ],
        "lave-vaisselle": [
            `Recharger le sel régénérant tous les 1 à 2 mois (eau ${water.label} de ${city.dept})`,
            `Nettoyer le filtre de cuve toutes les 2 semaines`,
            `Régler la dureté de l'eau sur "${Math.round(water.th / 1.78)}°dH" dans les paramètres machine`,
        ],
        "frigo": [
            `Dégivrer manuellement (modèles statiques) tous les 3 à 6 mois`,
            `Nettoyer la grille condenseur arrière 1× / an (poussières typiques des appartements ${city.name})`,
            `Remplacer le filtre à eau du distributeur tous les 6 mois (eau ${water.label})`,
        ],
        "four": [
            `Lancer la pyrolyse 1 à 2× par an (limite l'usure du joint de porte)`,
            `Vérifier la fermeture du joint à la flamme — défaut typique sur fours encastrés ${city.name}`,
            `Ne pas couvrir les grilles de ventilation arrière dans le caisson encastrement`,
        ],
        "seche-linge": [
            `Vider le bac à condensats après chaque cycle (humidité urbaine ${city.name})`,
            `Nettoyer le filtre à peluches systématiquement et le condenseur tous les 3 mois`,
            `Sortir la peluche du tuyau d'évacuation 2× par an sur les modèles à évacuation`,
        ],
    };
    return map[serviceKey] || [];
}

/* ─────────── 5. DETERMINISTIC LOCAL TESTIMONIALS ─────────── */
const FIRST_NAMES = ["Sophie", "Julien", "Camille", "Antoine", "Léa", "Nicolas", "Marie", "Thomas", "Émilie", "Pierre", "Charlotte", "Alexandre", "Sarah", "Olivier", "Caroline", "Mathieu", "Hélène", "Vincent", "Aurélie", "Benjamin"];

/**
 * Hash a string → integer (deterministic).
 */
function hashCode(s) {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h);
}

export function getLocalTestimonials(serviceKey, city) {
    const seed = hashCode(`${serviceKey}-${city.slug}`);
    const pick = (arr, offset) => arr[(seed + offset) % arr.length];

    const serviceTexts = {
        "lave-linge": [
            "Lave-linge qui fuyait sous le tambour. Le technicien est venu le lendemain matin, a remplacé le joint et la durite en moins d'une heure. Très propre, ponctuel et clair sur le devis.",
            "Mon lave-linge ne chauffait plus. Diagnostic en 15 minutes : résistance entartrée. Pièce changée sur place, garantie 18 mois, c'est exactement ce que je cherchais.",
            "Tambour bloqué, je pensais devoir racheter la machine. Le technicien a remplacé les roulements et la courroie, la machine tourne comme neuve. Super pro.",
        ],
        "lave-vaisselle": [
            "Lave-vaisselle qui ne vidangeait plus. Pompe nettoyée et durite remplacée, intervention rapide et soignée. Façade encastrée remontée parfaitement.",
            "Vaisselle qui ressortait sale et tachée. Le technicien a diagnostiqué un problème d'adoucisseur et de bras de lavage entartrés. Tout réglé en une seule visite.",
            "Code erreur E24 qui revenait sans cesse. Diagnostic clair, carte de puissance changée, plus aucun souci depuis. Vraiment satisfaite.",
        ],
        "frigo": [
            "Mon frigo ne refroidissait plus mais le congélateur fonctionnait. Diagnostic immédiat : ventilateur HS. Pièce changée sur place le jour même, parfait.",
            "Frigo américain qui ne faisait plus de glaçons. Filtre à eau bouché par le calcaire, kit changé, remise en service en 1 h. Technicien très pro.",
            "Bruit anormal du compresseur depuis des semaines. Le technicien a identifié un relais défectueux, réparé en moins d'une heure. Tarif honnête.",
        ],
        "four": [
            "Four qui ne chauffait plus, résistance grillée. Pièce remplacée sur place, four de nouveau opérationnel le jour même. Très satisfait.",
            "Pyrolyse en panne et porte qui ne se déverrouillait plus. Diagnostic précis, sécurité de porte remplacée, plus de souci.",
            "Carte électronique HS sur un four Bosch encastré. Devis raisonnable, intervention propre, four remis en route en 1h30.",
        ],
        "seche-linge": [
            "Sèche-linge à pompe à chaleur qui ne séchait plus correctement. Condenseur nettoyé en profondeur, cycle de nouveau en 1h30 au lieu de 4h. Bluffant.",
            "Sèche-linge qui s'arrêtait au milieu du cycle. Diagnostic : capteur de température défectueux. Réparé en 45 minutes, tarif clair.",
            "Bac à condensats qui ne se vidait plus. Pompe de relevage remplacée, machine de nouveau opérationnelle. Service au top.",
        ],
    };

    const texts = serviceTexts[serviceKey] || serviceTexts["lave-linge"];
    return [0, 1, 2].map((i) => ({
        name: pick(FIRST_NAMES, i),
        neighborhood: city.neighborhoods[(seed + i) % city.neighborhoods.length],
        text: texts[i],
        rating: 5,
    }));
}

/* ─────────── 6. EXTRA LOCAL FAQ ─────────── */
export function getExtraLocalFaq(serviceKey, city) {
    const water = getWaterHardness(city);
    const faqs = {
        "lave-linge": {
            q: `Le calcaire de l'eau de ${city.dept} abîme-t-il mon lave-linge ?`,
            a: `Oui. L'eau à ${city.name} est ${water.label} (${water.note}). Cela entartre la résistance, la pompe et le tambour, et raccourcit la durée de vie de la machine. Nous recommandons un détartrage tous les 3 mois et la vérification annuelle de la résistance lors d'une intervention.`,
        },
        "lave-vaisselle": {
            q: `Pourquoi ma vaisselle ressort tachée à ${city.name} ?`,
            a: `Dans la grande majorité des cas c'est l'eau ${water.label} de ${city.dept} (${water.note}) qui sature l'adoucisseur du lave-vaisselle. Nous vérifions le niveau de sel, le réglage de dureté de la machine, l'état des bras de lavage et le doseur de produit de rinçage lors du diagnostic.`,
        },
        "frigo": {
            q: `Mon frigo américain ne fait plus de glaçons à ${city.name}, est-ce le calcaire ?`,
            a: `Souvent oui. À ${city.name}, l'eau ${water.label} de ${city.dept} bouche le filtre à eau et l'électrovanne du circuit de glaçons. Le remplacement du filtre toutes les 6 mois est essentiel ; nous le faisons systématiquement lors de l'intervention.`,
        },
        "four": {
            q: `Combien de temps dure une intervention sur un four encastré à ${city.name} ?`,
            a: `En moyenne 1 h à 1 h 30 sur place, démontage du caisson inclus. Sur les fours encastrés en colonne typiques des cuisines de ${city.name}, nous prévoyons une marge pour démonter et remonter les façades sans rayure ni jeu.`,
        },
        "seche-linge": {
            q: `Pourquoi mon sèche-linge à pompe à chaleur prend 4 h à ${city.name} ?`,
            a: `Dans 9 cas sur 10, c'est l'encrassement du condenseur (échangeur thermique) et du filtre à peluches. L'humidité urbaine de ${city.name} accélère ce phénomène. Un nettoyage en profondeur ramène la durée de cycle à 1h30 — 2 h.`,
        },
    };
    return faqs[serviceKey] || null;
}
